import { useMemo } from "react";

import { Box } from "@mui/material";

import { ActionsProvider } from "src/context/Actions/provider";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { URLHandler } from "src/services/URLHandler";
import { useGetAllActionPlan } from "src/api/client-api/ActionPlan/ActionPlan/useGetAllActionPlan";
import { EmptyStateCard } from "src/components/EmptyState/EmptyStateCard";

import type { ActionPlanActionsListConfigProps } from "./types";
import { buildGridColumnsOptionsFromColumnsConfig } from "./types";
import { ListPlanning } from "src/components/Actions/Tabs/2_TabActionPlan/ActionList/List/ListPlanning";
import { useGetSsqVersionAssignment } from "src/api/client-api/questionnaire/useGetSsqVersionAssignment.ts";
import { useGetOneSsqVersion } from "src/api/sure-api/ssq/useGetOneSsqVersion.ts";

export const ActionPlanActionsList = (props: ActionPlanActionsListConfigProps) => {
    const { columns } = props;
    const { selectedCompany, currentCampaign } = useFoodpilotContext();

    const companyId = selectedCompany?.id ? Number(selectedCompany.id) : null;
    const ssqId =
        selectedCompany?.organizationSector?.primarySsqId ?? selectedCompany?.organizationSector?.ssqId ?? undefined;

    const {
        ssqVersionAssignment,
        isReady: isAssignmentReady,
        isError,
    } = useGetSsqVersionAssignment(ssqId, currentCampaign?.id);

    const ssqVersionId = ssqVersionAssignment?.ssqVersionId;

    const { ssqVersion, isReady: isSsqVersionReady } = useGetOneSsqVersion(ssqVersionId);

    const {
        data: actionPlans,
        isFetched: areActionPlansFetched,
        isLoading: areActionPlansLoading,
    } = useGetAllActionPlan({
        queryParam:
            companyId !== null && ssqId !== null ?
                {
                    "startSsqPost.organization.id": companyId,
                    "startSsqPost.ssqId": ssqId,
                }
            :   undefined,
        enabled: companyId !== null && ssqId !== null && !isError && isAssignmentReady && isSsqVersionReady,
    });

    const gridColumnsOptions = useMemo(() => {
        return buildGridColumnsOptionsFromColumnsConfig(columns);
    }, [columns]);

    if (companyId === null || ssqId === null) {
        return <EmptyStateCard title={"Unable to render actions list"} description={"Company context is missing."} />;
    }

    if (!isAssignmentReady || !isSsqVersionReady) {
        return <Box />;
    }

    if (isError || !ssqVersion) {
        return (
            <EmptyStateCard title={"Unable to render actions list"} description={"Questionnaire context is missing."} />
        );
    }

    if (areActionPlansLoading) {
        return <Box />;
    }

    if (!areActionPlansFetched) {
        return <Box />;
    }

    if (areActionPlansFetched && (actionPlans?.length ?? 0) === 0) {
        return (
            <EmptyStateCard
                title={"No action plan available"}
                description={"This company does not have an action plan for the current questionnaire."}
            />
        );
    }

    const ssq = ssqVersion.ssq;

    return (
        <ActionsProvider
            ssqVersion={ssqVersion}
            extraFilterForAction={{
                "startSsqPost.organization.id": companyId,
                "startSsqPost.ssqId": ssqId,
            }}
            urls={{
                noActionPlan: () => URLHandler.company.actions.noActionPlan(companyId, ssq.id),
                planning: () => URLHandler.company.actions.planning(companyId, ssq.id),
                trajectory: () => URLHandler.company.actions.trajectory(companyId, ssq.id),
                trajectoryBarChart: () => URLHandler.company.actions.trajectoryBarChart(companyId, ssq.id),
                trajectoryCards: () => URLHandler.company.actions.trajectoryCards(companyId, ssq.id),
                catalog: () => URLHandler.company.actions.catalog(companyId, ssq.id),
                catalogList: () => URLHandler.company.actions.catalogList(companyId, ssq.id),
                catalogGroup: (actionId: number) =>
                    URLHandler.company.actions.catalogGroup(companyId, ssq.id, actionId),
                catalogGroupItem: (groupId: number, actionId: number) =>
                    URLHandler.company.actions.catalogGroupItem(companyId, ssq.id, groupId, actionId),
                editPlanning: (actionId: string) =>
                    URLHandler.company.actions.editPlanning(companyId, ssq.id, actionId),
                editCatalog: (actionId: string) => URLHandler.company.actions.editCatalog(companyId, ssq.id, actionId),
            }}
        >
            <ListPlanning showWaterfall={false} gridColumnsOptions={gridColumnsOptions} allowDelete={false} />
        </ActionsProvider>
    );
};

export default ActionPlanActionsList;
